"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FuturesUserBalance = exports.SpotUserBalance = exports.UserBalance = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const shared_1 = require("../types/binance/websockets/shared");
const spot_1 = require("../types/binance/websockets/spot");
const formatted_1 = require("../utils/eventFilters/formatted");
const misc_1 = require("../utils/misc");
const constants_1 = require("../config/constants");
class UserBalance {
    constructor(params) {
        this.assetsData = new Map();
        this.totalQuoteAssets = [misc_1.EAssets.USDT];
        this.assetsUpdateStats = {
            assetsNull: new Map(),
            assetsNew: new Map(),
        };
        const { initialAssets, marketData, totalQuoteAssets } = params;
        if (totalQuoteAssets && totalQuoteAssets.length > 0)
            this.totalQuoteAssets = totalQuoteAssets;
        this.initialAssets = new Map(initialAssets.map(asset => [asset.assetId, asset]));
        this.marketData = marketData;
        this.assetsData = new Map(initialAssets.map(asset => [asset.assetId, Object.assign({ quoteBalance: {} }, asset)]));
        this.update({
            marketData: this.marketData,
        });
    }
    static getSelfBalance(balanceAsset) {
        const stablecoinBalance = misc_1.KNOWN_STABLECOINS.includes(balanceAsset.assetId) ? {
            'USDT': balanceAsset.assetBalance
        } : {};
        return Object.assign({ [balanceAsset.assetId]: balanceAsset.assetBalance }, stablecoinBalance);
    }
    static mergeBalanceWithMarket(balanceAsset, marketAsset) {
        const selfBalance = UserBalance.getSelfBalance(balanceAsset);
        if (marketAsset === undefined)
            return Object.assign(Object.assign({}, balanceAsset), { quoteBalance: Object.assign(Object.assign({}, balanceAsset.quoteBalance), selfBalance) });
        if (balanceAsset.assetId !== marketAsset.assetId)
            return balanceAsset;
        const marketBalances = Object.entries(marketAsset.quotes)
            .reduce((acc, [quoteId, quotePrice]) => {
            return Object.assign(Object.assign({}, acc), { [quoteId]: new bignumber_js_1.default(balanceAsset.assetBalance).multipliedBy(quotePrice).toNumber() });
        }, {});
        return Object.assign(Object.assign({}, balanceAsset), { quoteBalance: Object.assign(Object.assign(Object.assign({}, balanceAsset.quoteBalance), selfBalance), marketBalances) });
    }
    calcAssets(assets, marketData) {
        // KNOWN_STABLECOINS calculated as 1:1
        const updatedAssets = Array.from(assets).map(([assetKey, assetValue]) => {
            const marketAsset = marketData.marketAssets.get(assetKey);
            return UserBalance.mergeBalanceWithMarket(assetValue, marketAsset);
        });
        return new Map(updatedAssets.filter(asset => {
            return asset.assetBalance !== 0;
        }).map(asset => {
            return [asset.assetId, asset];
        }));
    }
    update(params) {
        const { marketData, events } = params;
        this.marketData = marketData || this.marketData;
        const assetsFromEvents = this.getBalancesFromEvents(events);
        this.assetsUpdateStats.assetsNew.clear();
        this.assetsUpdateStats.assetsNull.clear();
        // seed balances from balance events
        Array.from(assetsFromEvents, ([, value]) => value).forEach(asset => {
            const { assetId, assetBalance } = asset;
            // subscribe assets if got unknown asset in event
            if (this.assetsData.get(assetId) === undefined && !misc_1.KNOWN_STABLECOINS.includes(assetId))
                this.assetsUpdateStats.assetsNew.set(assetId, assetId);
            // unsubscribe assets
            if (Number(assetBalance) === 0)
                this.assetsUpdateStats.assetsNull.set(assetId, assetId);
            else
                this.assetsUpdateStats.assetsNull.delete(assetId);
            this.assetsData.set(assetId, Object.assign(Object.assign({}, asset), { quoteBalance: {} }));
        });
        this.assetsData = this.calcAssets(this.assetsData, this.marketData);
    }
    countAssetTotal(asset) {
        return Array.from(this.assetsData, ([, value]) => value).reduce((acc, value) => {
            if (acc === undefined || value.quoteBalance[asset] === undefined)
                return undefined;
            return acc.plus(value.quoteBalance[asset] || 0);
        }, constants_1.BN_ZERO);
    }
    get assetsTotal() {
        const quoteAssetPrimary = this.totalQuoteAssets[0] || misc_1.EAssets.USDT;
        const assetBaseValue = this.countAssetTotal(quoteAssetPrimary);
        return this.totalQuoteAssets.reduce((acc, quoteAsset) => {
            var _a;
            let assetValue = undefined;
            if (misc_1.KNOWN_STABLECOINS.includes(quoteAsset) || quoteAssetPrimary === quoteAsset)
                assetValue = assetBaseValue;
            else {
                const quoteMarketPrice = (_a = this.marketData.marketAssets.get(quoteAsset)) === null || _a === void 0 ? void 0 : _a.quotes[quoteAssetPrimary];
                assetValue = assetBaseValue && quoteMarketPrice !== undefined ? assetBaseValue.dividedBy(quoteMarketPrice) : undefined;
            }
            return {
                totalAssetsSource: Object.assign(Object.assign({}, acc.totalAssetsSource), { [quoteAsset]: assetValue }),
                totalAssetsOutput: Object.assign(Object.assign({}, acc.totalAssetsOutput), { [quoteAsset]: assetValue !== undefined ? assetValue.toNumber() : assetValue }),
            };
        }, {
            totalAssetsSource: {},
            totalAssetsOutput: {},
        });
    }
    get assetsDataArray() {
        return Array.from(this.assetsData, ([, value]) => value);
    }
    get assetIds() {
        return this.assetsDataArray.map(asset => {
            return asset.assetId;
        });
    }
    get data() {
        return Object.assign({ assetsData: this.assetsData, assetsDataArray: this.assetsDataArray, assetsUpdateStats: this.assetsUpdateStats }, this.assetsTotal);
    }
}
exports.UserBalance = UserBalance;
class SpotUserBalance extends UserBalance {
    getBalancesFromEvents(events) {
        if (events === undefined)
            return new Map();
        const balanceEvents = formatted_1.eventFormattedFiltersMap[spot_1.EWsBinanceSpotEventsTypes.outboundAccountPosition](events);
        return new Map(balanceEvents
            .map(outboundAccEvent => {
            return outboundAccEvent.balances.map(eventBalance => {
                return {
                    assetId: eventBalance.asset,
                    assetBalance: Number(eventBalance.free),
                };
            });
        })
            .flatMap(balance => balance)
            .map(balance => {
            return [balance.assetId, balance];
        }));
    }
}
exports.SpotUserBalance = SpotUserBalance;
class FuturesUserBalance extends UserBalance {
    getBalancesFromEvents(events) {
        if (events === undefined)
            return new Map();
        const balanceEvents = formatted_1.eventFormattedFiltersMap[shared_1.EWsBinanceFuturesEventTypes.ACCOUNT_UPDATE](events);
        return new Map(balanceEvents
            .map(outboundAccEvent => {
            return outboundAccEvent.updateData.updatedBalances.map(eventBalance => {
                return {
                    assetId: eventBalance.asset,
                    assetBalance: Number(eventBalance.walletBalance),
                };
            });
        })
            .flatMap(balance => balance)
            .map(balance => {
            return [balance.assetId, balance];
        }));
    }
}
exports.FuturesUserBalance = FuturesUserBalance;
